import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/samples')
const BOOKS_API_BASE_URL = apiUrl('/books')
const GET_ALL_SAMPLES_ENDPOINT = `${API_BASE_URL}/all`
const GET_SAMPLE_ENDPOINT = `${API_BASE_URL}/getSample`
const ADD_SAMPLE_ENDPOINT = `${API_BASE_URL}/add`
const DELETE_SAMPLE_ENDPOINT = `${API_BASE_URL}/deleteSample`
const APPROVE_SAMPLE_ENDPOINT = `${API_BASE_URL}/approve`
const UNAPPROVE_SAMPLE_ENDPOINT = `${API_BASE_URL}/unapprove`
const GET_BOOKS_BY_TYPE_ENDPOINT = `${BOOKS_API_BASE_URL}/type`
const GET_APPROVED_SAMPLES_ENDPOINT = `${API_BASE_URL}/approved`
const RELEASE_SAMPLE_ENDPOINT = `${API_BASE_URL}/release`
const RETURN_SAMPLE_ENDPOINT = `${API_BASE_URL}/return`

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Sample request failed (${response.status})`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  if (response.status === 204) return null
  return response.json()
}

function getSampleList(data) {
  if (Array.isArray(data)) return data
  return data?.samples || data?.sampleList || data?.results || data?.data || []
}

function getBookList(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of [
    'books',
    'bookList',
    'booklist',
    'publishedBooks',
    'published_books',
    'results',
    'items',
    'rows',
    'result',
    'payload',
    'data',
  ]) {
    const list = getBookList(data[key])
    if (list.length) return list
  }

  for (const value of Object.values(data)) {
    const list = getBookList(value)
    if (list.length && list.every((item) => item && typeof item === 'object'))
      return list
  }

  return []
}

function findNextCursor(data) {
  if (!data || typeof data !== 'object') return null
  return (
    data.nextCursor ??
    data.next_cursor ??
    data.nextPageToken ??
    data.next_page_token ??
    data.next ??
    findNextCursor(data.samples) ??
    findNextCursor(data.sampleList) ??
    findNextCursor(data.results) ??
    findNextCursor(data.items) ??
    findNextCursor(data.data) ??
    findNextCursor(data.pagination) ??
    findNextCursor(data.meta) ??
    null
  )
}

export async function getSamples({ cursor = null, page = 1 } = {}) {
  const params = new URLSearchParams({ limit: '20' })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))

  const data = await request(`${GET_ALL_SAMPLES_ENDPOINT}?${params.toString()}`)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const samples = getSampleList(data)
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ??
      payload?.currentPage ??
      payload?.current_page ??
      data?.page ??
      data?.currentPage ??
      data?.current_page ??
      page,
  )
  const totalPages = Number(
    payload?.totalPages ??
      payload?.total_pages ??
      data?.totalPages ??
      data?.total_pages ??
      0,
  )
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    data?.hasNext ??
    data?.has_next ??
    nextCursor ??
    totalPages > currentPage,
  )

  return {
    samples,
    nextCursor,
    hasNext,
    currentPage,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
  }
}

export async function getSample(id) {
  const data = await request(`${GET_SAMPLE_ENDPOINT}/${encodeURIComponent(id)}`)
  const sample = data?.sample || data?.data || data

  return {
    ...(sample && typeof sample === 'object' ? sample : {}),
    DeliveredBooks: data?.DeliveredBooks || data?.deliveredBooks || [],
    returnedBooks: data?.returnedBooks || data?.ReturnedBooks || [],
  }
}

export async function getBooksByType(bookType) {
  if (!bookType) throw new Error('Book type is required.')

  const data = await request(
    `${GET_BOOKS_BY_TYPE_ENDPOINT}/${encodeURIComponent(bookType)}`,
  )
  return getBookList(data)
}

export async function createSample(sample) {
  const data = await request(ADD_SAMPLE_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(sample),
  })
  return data?.sample || data?.data || data
}

export async function deleteSample(id) {
  await request(`${DELETE_SAMPLE_ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function approveSample(id, details) {
  const payload = { id, ...details }
  const data = await request(APPROVE_SAMPLE_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return data?.sample || data?.data || data
}

export async function unapproveSample(id, status) {
  const payload = { id, status }
  const data = await request(UNAPPROVE_SAMPLE_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return data?.sample || data?.data || data
}

export async function releaseSample(id, details) {
  const data = await request(RELEASE_SAMPLE_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({ id, status: 'delivered', ...details }),
  })
  return data?.sample || data?.data || data
}

export async function returnSample(id, details) {
  const data = await request(RETURN_SAMPLE_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({ id, status: 'returned', ...details }),
  })
  return data?.sample || data?.data || data
}

export async function getAllApprovedSamples() {
  const data = await request(GET_APPROVED_SAMPLES_ENDPOINT)
  return getSampleList(data)
}
